import { useNavigate, useLocation } from "react-router-dom";
import { useActiveOrg } from "@/contexts/ActiveOrgContext";
import { useLicence } from "@/hooks/useLicence";
import { useModuleFavorites } from "@/hooks/useModuleFavorites";
import {
  LayoutDashboard, FolderOpen, FileSpreadsheet, FileText,
  CheckSquare, GitMerge, Download, BookOpen, Archive,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState, useRef } from "react";

interface QuickNavItem {
  key: string;
  label: string;
  icon: React.ElementType;
  path: string;
  feature: string | null;
  orgTypes?: ("cabinet" | "entreprise")[];
}

const ALL_ITEMS: QuickNavItem[] = [
  { key: "dashboard", label: "Tableau de bord", icon: LayoutDashboard, path: "/dashboard", feature: null },
  { key: "dossiers", label: "Dossiers", icon: FolderOpen, path: "/dossiers", feature: null, orgTypes: ["cabinet"] },
  { key: "exercices", label: "Exercices", icon: BookOpen, path: "/dossiers", feature: null, orgTypes: ["entreprise"] },
  { key: "etats", label: "États financiers", icon: FileSpreadsheet, path: "/etats-financiers", feature: "montage_social", orgTypes: ["cabinet"] },
  { key: "pointage", label: "Pointage DGI", icon: FileText, path: "/pointage-dgi", feature: "pointage_efi_pdf", orgTypes: ["cabinet"] },
  { key: "verification", label: "Vérification", icon: CheckSquare, path: "/verification", feature: null, orgTypes: ["cabinet"] },
  { key: "consolidation", label: "Consolidation", icon: GitMerge, path: "/consolidation", feature: "montage_consolide", orgTypes: ["cabinet"] },
  { key: "archives", label: "Archives", icon: Archive, path: "/archives", feature: null, orgTypes: ["cabinet"] },
  { key: "exports", label: "Exports", icon: Download, path: "/exports", feature: null, orgTypes: ["cabinet"] },
  { key: "settings", label: "Paramètres", icon: Settings, path: "/settings", feature: null },
];

export { ALL_ITEMS };
export type { QuickNavItem };

export function ModuleQuickNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrg } = useActiveOrg();
  const { features } = useLicence();
  const { favorites, reorderFavorites } = useModuleFavorites();

  const orgType = activeOrg?.organization?.type as "cabinet" | "entreprise" | undefined;

  const draggedKey = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  // Sort visible items by favorites order
  const visibleItems = ALL_ITEMS
    .filter((item) => {
      if (!favorites.includes(item.key)) return false;
      if (item.orgTypes && orgType && !item.orgTypes.includes(orgType)) return false;
      if (item.feature && !features[item.feature]) return false;
      return true;
    })
    .sort((a, b) => favorites.indexOf(a.key) - favorites.indexOf(b.key));

  if (visibleItems.length === 0) return null;

  return (
    <nav className="h-9 border-b border-border/40 bg-card/50 backdrop-blur-sm flex items-center px-4 gap-0.5 shrink-0 overflow-x-auto scrollbar-hide">
      {visibleItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
        const isDragOver = dropTarget === item.key;

        return (
          <button
            key={item.key}
            draggable
            onDragStart={(e) => {
              draggedKey.current = item.key;
              e.dataTransfer.effectAllowed = "move";
              // Make the drag ghost semi-transparent
              if (e.currentTarget) {
                e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (draggedKey.current && draggedKey.current !== item.key) {
                setDropTarget(item.key);
              }
            }}
            onDragLeave={() => {
              if (dropTarget === item.key) setDropTarget(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedKey.current && draggedKey.current !== item.key) {
                reorderFavorites(draggedKey.current, item.key);
              }
              draggedKey.current = null;
              setDropTarget(null);
            }}
            onDragEnd={() => {
              draggedKey.current = null;
              setDropTarget(null);
            }}
            onClick={() => navigate(item.path)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-grab active:cursor-grabbing",
              isActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
              isDragOver && "ring-1 ring-primary/40 bg-primary/5"
            )}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
